import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Fragment, useEffect, useState } from "react";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { CaretDown, List, X } from "@phosphor-icons/react";
import api from "../lib/api";
import useSiteContent, { useSiteContentReady, getCachedSiteContent } from "../lib/useSiteContent";
import { useLanguage } from "../hooks/useLanguage";
import { useSectorLabels } from "../lib/useSectorLabels";
import Logo from "../components/Logo";
import NotificationBell from "../components/NotificationBell";
import ThemeToggle from "../components/ThemeToggle";
import LanguageToggle from "../components/LanguageToggle";

// Stable list of top-level public nav slot ids, in their DEFAULT order.
// Admin can reorder them via Site Content -> Public site navigation ->
// "nav_menu_order" (drag-and-drop, saved as a comma-separated id string) —
// see the matching NAV_ORDER_ITEMS list in Admin.jsx, which MUST stay in
// sync with this array if a new top-level nav item is ever added.
const NAV_ITEM_IDS = ["area_of_interest", "why_oodel", "editorial", "public_sentiment", "waves", "customer_sentiment"];

function getOrderedNavIds(orderStr) {
  if (!orderStr) return NAV_ITEM_IDS;
  const parsed = orderStr.split(",").map((s) => s.trim()).filter((id) => NAV_ITEM_IDS.includes(id));
  const missing = NAV_ITEM_IDS.filter((id) => !parsed.includes(id));
  return [...parsed, ...missing];
}

export default function PublicNav() {
  const { user, loading, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const content = useSiteContent();
  const siteReady = useSiteContentReady();
  const { lang } = useLanguage();
  const { sectorLabel } = useSectorLabels();
  // First-ever visit (cleared cache) shows an empty object until /site-content
  // resolves. In that window, every `content.nav_*_enabled !== "false"` check
  // would be `undefined !== "false"` → true, briefly rendering ALL nav links
  // before admin-hidden ones disappear. Gate the nav body on `navReady` to
  // suppress that flash. Returning visitors keep instant paint via cache.
  const [hadInitialCache] = useState(() => Object.keys(getCachedSiteContent()).length > 0);
  const navReady = hadInitialCache || siteReady;
  const [sectors, setSectors] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  useEffect(() => {
    // Sectors are now dynamic — pulled from the CMS-backed /api/sectors endpoint
    // so admin can rename / reorder / hide without redeploy.
    api.get("/sectors").then((r) => setSectors((r.data || []).filter((s) => s.visible_on_homepage !== false))).catch(() => {});
  }, []);
  const isAdmin = user?.role === "admin";
  const isPanelist = user?.role === "panelist" || user?.role === "patron";
  const hasAnyAccess = !!(user?.has_global_intelligence_access || user?.has_private_vault_access);
  const showHub = !isAdmin && hasAnyAccess;
  const showPanel = !isAdmin && !hasAnyAccess && isPanelist;
  const onLogout = () => { logout(); nav("/"); setDrawerOpen(false); };
  const close = () => setDrawerOpen(false);
  // T(key, fallback) — CMS-overridable label with safe fallback.
  const T = (k, fallback) => {
    const v = content[k];
    return v !== undefined && v !== null && String(v).trim() !== "" ? v : fallback;
  };
  // Public Sentiment is now a top-level nav item. Two gates:
  //   - feature_public_sentiment_enabled : kills the entire feature
  //   - nav_public_sentiment_enabled     : show/hide just the nav item
  const sentimentNavOn =
    content.feature_public_sentiment_enabled !== "false" &&
    content.nav_public_sentiment_enabled !== "false";
  // Anonymous (logged-out) visitors never get the language toggle,
  // on ANY public page — keeps every logged-out page focused on the
  // sign-up CTA. Once a patron logs in, it appears everywhere (including
  // the homepage) since panelists need it to read Urdu survey/report
  // content across the whole app.
  const showLangToggle = !!user;
  const orderedNavIds = getOrderedNavIds(content.nav_menu_order);

  // Desktop nav items keyed by slot id — rendered in admin-chosen order below.
  const navItems = {
    area_of_interest: navReady && content.nav_area_of_interest_enabled !== "false" && (
      <NavMenu label={T("nav_label_area_of_interest", "Area of Interest")} testid="nav-sectors">
        <div className="grid grid-cols-2 gap-1 p-2 w-[420px]">
          {sectors.map((s) => (
            <Link key={s.key} to={`/sectors/${s.key}`} className="px-3 py-2 hover:bg-neutral-100 text-sm font-medium" data-testid={`sector-link-${s.key}`}>{s.icon} {sectorLabel(s, lang)}</Link>
          ))}
        </div>
      </NavMenu>
    ),
    why_oodel: navReady && content.nav_why_oodel_enabled !== "false" && (
      <NavMenu label={T("nav_label_why_oodel", "Why oodel")} testid="nav-why">
        <div className="p-2 w-[280px]">
          {content.nav_for_patrons_enabled !== "false" && <Link to="/for-patrons" className="block px-3 py-2 hover:bg-neutral-100 text-sm">{T("nav_label_for_patrons", "For Patrons")}</Link>}
          {content.nav_for_businesses_enabled !== "false" && <Link to="/for-businesses" className="block px-3 py-2 hover:bg-neutral-100 text-sm">{T("nav_label_for_businesses", "For Businesses")}</Link>}
          {content.nav_methodology_enabled !== "false" && <Link to="/methodology" className="block px-3 py-2 hover:bg-neutral-100 text-sm">{T("nav_label_methodology", "Our Methodology")}</Link>}
        </div>
      </NavMenu>
    ),
    editorial: navReady && content.nav_editorial_enabled !== "false" && (
      <NavMenu label={T("nav_label_editorial", "Editorial")} testid="nav-editorial">
        <div className="p-2 w-[260px]">
          {content.nav_reports_articles_enabled !== "false" && <Link to="/articles" className="block px-3 py-2 hover:bg-neutral-100 text-sm">{T("nav_label_reports_articles", "Reports & Articles")}</Link>}
          {content.nav_deep_dives_enabled !== "false" && <Link to="/articles?has_audio=1" className="block px-3 py-2 hover:bg-neutral-100 text-sm">{T("nav_label_deep_dives", "Deep Dives (Audio)")}</Link>}
          {content.nav_all_trackers_enabled !== "false" && <Link to="/trackers" className="block px-3 py-2 hover:bg-neutral-100 text-sm">{T("nav_label_all_trackers", "All Trackers")}</Link>}
          {content.nav_snapshots_enabled !== "false" && <Link to="/snapshots" className="block px-3 py-2 hover:bg-neutral-100 text-sm">{T("nav_label_snapshots", "Snapshots")}</Link>}
        </div>
      </NavMenu>
    ),
    public_sentiment: navReady && sentimentNavOn && (
      <Link to="/sentiment" className="text-sm font-medium text-neutral-700 hover:text-black" data-testid="nav-sentiment">{T("nav_label_public_sentiment", "Public Sentiment")}</Link>
    ),
    waves: navReady && content.nav_waves_enabled === "true" && (
      <Link to="/waves" className="text-sm font-medium text-neutral-700 hover:text-black" data-testid="nav-waves">{T("nav_label_waves", "Waves")}</Link>
    ),
    customer_sentiment: navReady && content.nav_customer_sentiment_enabled !== "false" && (
      <Link to="/oodelscore" className="text-sm font-medium text-neutral-700 hover:text-black" data-testid="nav-customer-sentiment">{T("nav_label_customer_sentiment", "oodel SCORE")}</Link>
    ),
  };

  // Mobile drawer nav sections, same slot ids, same admin-chosen order.
  const mobileNavItems = {
    area_of_interest: navReady && content.nav_area_of_interest_enabled !== "false" && (
      <MobileGroup title={T("nav_label_area_of_interest", "Area of Interest")}>
        {sectors.map((s) => (
          <Link key={s.key} to={`/sectors/${s.key}`} onClick={close} className="block px-5 py-3 text-base hover:bg-neutral-100 active:bg-neutral-200 border-l-2 border-transparent hover:border-mango transition-colors" data-testid={`mobile-sector-${s.key}`}>{s.icon} {sectorLabel(s, lang)}</Link>
        ))}
      </MobileGroup>
    ),
    why_oodel: navReady && content.nav_why_oodel_enabled !== "false" && (
      <MobileGroup title={T("nav_label_why_oodel", "Why oodel")}>
        {content.nav_for_patrons_enabled !== "false" && <MobileLink to="/for-patrons" onClick={close}>{T("nav_label_for_patrons", "For Patrons")}</MobileLink>}
        {content.nav_for_businesses_enabled !== "false" && <MobileLink to="/for-businesses" onClick={close}>{T("nav_label_for_businesses", "For Businesses")}</MobileLink>}
        {content.nav_methodology_enabled !== "false" && <MobileLink to="/methodology" onClick={close}>{T("nav_label_methodology", "Our Methodology")}</MobileLink>}
      </MobileGroup>
    ),
    editorial: navReady && content.nav_editorial_enabled !== "false" && (
      <MobileGroup title={T("nav_label_editorial", "Editorial")}>
        {content.nav_reports_articles_enabled !== "false" && <MobileLink to="/articles" onClick={close}>{T("nav_label_reports_articles", "Reports & Articles")}</MobileLink>}
        {content.nav_deep_dives_enabled !== "false" && <MobileLink to="/articles?has_audio=1" onClick={close}>{T("nav_label_deep_dives", "Deep Dives (Audio)")}</MobileLink>}
        {content.nav_all_trackers_enabled !== "false" && <MobileLink to="/trackers" onClick={close}>{T("nav_label_all_trackers", "All Trackers")}</MobileLink>}
        {content.nav_snapshots_enabled !== "false" && <MobileLink to="/snapshots" onClick={close}>{T("nav_label_snapshots", "Snapshots")}</MobileLink>}
      </MobileGroup>
    ),
    public_sentiment: navReady && sentimentNavOn && (
      <MobileGroup title={T("nav_label_public_sentiment", "Public Sentiment")}>
        <MobileLink to="/sentiment" onClick={close}>{T("nav_label_public_sentiment_link", "Browse ratings")}</MobileLink>
      </MobileGroup>
    ),
    waves: navReady && content.nav_waves_enabled === "true" && (
      <MobileGroup title={T("nav_label_waves", "Waves")}>
        <MobileLink to="/waves" onClick={close}>{T("nav_label_waves", "Waves")}</MobileLink>
      </MobileGroup>
    ),
    customer_sentiment: navReady && content.nav_customer_sentiment_enabled !== "false" && (
      <MobileGroup title={T("nav_label_customer_sentiment", "oodel SCORE")}>
        <MobileLink to="/oodelscore" onClick={close}>{T("nav_label_customer_sentiment_link", "Browse oodel SCORE →")}</MobileLink>
      </MobileGroup>
    ),
  };

  return (
    <header className="border-b border-grid bg-white sticky top-0 z-40 themable-public">
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 h-16 flex items-center justify-between gap-4">
        <Logo to="/" size="md" data-testid="public-logo" suffix={<span className="hidden md:inline overline text-neutral-500" style={{ fontSize: '0.55rem' }}>{content.nav_subtitle || "Market Intelligence Pakistan"}</span>} />

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-7">
          {orderedNavIds.map((id) => <Fragment key={id}>{navItems[id]}</Fragment>)}
        </nav>

        {/* Right-side actions */}
        <div className="flex items-center gap-2 md:gap-3">
          {/* Language toggle — hidden from anonymous visitors on every
              public page (see showLangToggle above); only appears once
              a patron is logged in. */}
          {showLangToggle && <LanguageToggle className="hidden md:inline-flex" />}
          {/* Theme toggle — always visible (anon + logged-in) */}
          <ThemeToggle variant="minimal" className="hidden md:inline-flex" />
          {/* During the initial auth bootstrap we don't yet know whether the
              user is signed in — render an invisible spacer instead of
              flashing the logged-out CTAs (Login / Join Panel). This kills
              the menu-flicker users were seeing right after refresh. */}
          {loading ? (
            <div className="hidden sm:inline-flex w-[180px] h-10" aria-hidden="true" />
          ) : user ? (
            <>
              <NotificationBell />
              {isAdmin && <Link to="/admin" className="hidden sm:inline-flex"><Button variant="outline" className="rounded-none" data-testid="nav-admin">{T("nav_label_cms", "CMS")}</Button></Link>}
              {showHub && <Link to="/enterprise" className="hidden sm:inline-flex"><Button className="rounded-none bg-mango hover:bg-oodel-dark text-white border-2 border-black" data-testid="nav-hub">{T("nav_label_my_hub", "My Hub")}</Button></Link>}
              {showPanel && <Link to="/panelist" className="hidden sm:inline-flex"><Button variant="outline" className="rounded-none" data-testid="nav-panelist">{T("nav_label_my_panel", "My Panel")}</Button></Link>}
              <Button data-testid="nav-logout" onClick={onLogout} className="hidden sm:inline-flex rounded-none bg-black hover:bg-neutral-800">{T("nav_label_logout", "Logout")}</Button>
            </>
          ) : (
            <>
              {content.nav_login_enabled !== "false" && <Link to="/login" state={{ from: loc.pathname }} className="hidden sm:inline-flex"><Button data-testid="nav-login" variant="ghost" className="rounded-none">{T("nav_label_login", "Login")}</Button></Link>}
              {content.nav_join_panel_enabled !== "false" && <Link to="/register" className="hidden sm:inline-flex"><Button data-testid="nav-join" className="rounded-none bg-black hover:bg-neutral-800">{T("nav_label_join_panel", "Join Panel")}</Button></Link>}
            </>
          )}

          {/* Mobile hamburger — visible <lg */}
          <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
            <SheetTrigger asChild>
              <button data-testid="mobile-menu-trigger" aria-label="Open menu" className="lg:hidden w-10 h-10 inline-flex items-center justify-center border-2 border-black bg-white hover:bg-neutral-100 transition-colors">
                <List size={20} weight="bold" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[88vw] max-w-sm p-0 border-0 bg-white overflow-y-auto" data-testid="mobile-drawer">
              <SheetHeader className="p-5 border-b-2 border-black flex flex-row items-center justify-between space-y-0">
                <SheetTitle asChild>
                  <Logo to="/" size="md" data-testid="mobile-drawer-logo" />
                </SheetTitle>
              </SheetHeader>

              {/* Account actions at top — most important on mobile */}
              <div className="p-5 border-b border-neutral-200 space-y-2">
                {loading ? (
                  <div className="h-12" aria-hidden="true" />
                ) : user ? (
                  <>
                    {isAdmin && <Link to="/admin" onClick={close}><Button className="w-full rounded-none bg-black hover:bg-neutral-800" data-testid="mobile-nav-admin">{T("nav_label_open_cms", "Open CMS")}</Button></Link>}
                    {showHub && <Link to="/enterprise" onClick={close}><Button className="w-full rounded-none bg-mango hover:bg-oodel-dark text-white border-2 border-black" data-testid="mobile-nav-hub">{T("nav_label_my_hub", "My Hub")}</Button></Link>}
                    {showPanel && <Link to="/panelist" onClick={close}><Button variant="outline" className="w-full rounded-none" data-testid="mobile-nav-panelist">{T("nav_label_my_panel", "My Panel")}</Button></Link>}
                    <Button onClick={onLogout} variant="ghost" className="w-full rounded-none text-neutral-600 justify-start" data-testid="mobile-nav-logout">{T("nav_label_sign_out", "Sign out")}</Button>
                  </>
                ) : (
                  <>
                    {content.nav_join_panel_enabled !== "false" && <Link to="/register" onClick={close}><Button className="w-full rounded-none bg-mango hover:bg-oodel-dark text-white border-2 border-black hard-shadow" data-testid="mobile-nav-join">{T("nav_label_join_panel_mobile", "Join the Panel →")}</Button></Link>}
                    {content.nav_login_enabled !== "false" && <Link to="/login" state={{ from: loc.pathname }} onClick={close}><Button variant="outline" className="w-full rounded-none" data-testid="mobile-nav-login">{T("nav_label_sign_in", "Sign in")}</Button></Link>}
                  </>
                )}
                {/* Language + theme toggle — language hidden for anon
                    visitors on the homepage, same rule as desktop */}
                {showLangToggle && <LanguageToggle className="w-full justify-center" />}
                <ThemeToggle className="w-full justify-center" />
              </div>

              {/* Mobile menu sections — clearly grouped, large tap targets */}
              <nav className="py-2" data-testid="mobile-nav-links">
                {orderedNavIds.map((id) => <Fragment key={id}>{mobileNavItems[id]}</Fragment>)}
              </nav>
              <div className="p-5 text-xs text-neutral-400 border-t border-neutral-200">{content.nav_subtitle || "Market Intelligence Pakistan"}</div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}

function MobileGroup({ title, children }) {
  return (
    <div className="py-2">
      <div className="px-5 pt-3 pb-2 overline text-mango">{title}</div>
      <div>{children}</div>
    </div>
  );
}

function MobileLink({ to, children, onClick }) {
  return (
    <Link to={to} onClick={onClick} className="block px-5 py-3 text-base hover:bg-neutral-100 active:bg-neutral-200 border-l-2 border-transparent hover:border-mango transition-colors">{children}</Link>
  );
}

function NavMenu({ label, testid, children }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="text-sm font-medium text-neutral-700 hover:text-black flex items-center gap-1 outline-none" data-testid={testid}>
        {label} <CaretDown size={12} />
      </DropdownMenuTrigger>
      <DropdownMenuContent className="rounded-none border-2 border-black p-0" align="start">
        {children}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * Renders PublicNav only when the current page is NOT nested under a
 * portal layout (panelist / admin / enterprise) that already supplies its own header.
 */
export function OuterNav() {
  const loc = useLocation();
  const inPortal = ["/panelist", "/admin", "/enterprise", "/business"].some((p) => loc.pathname.startsWith(p));
  if (inPortal) return null;
  return <PublicNav />;
}
